/**
 * F-505-FE-08: ThemeContext
 *
 * RTL/LTR direction management:
 * - Sets the `dir` attribute on document.documentElement
 * - Provides `useDirection()` hook for direction-aware logic
 * - Derives direction from language code (Arabic, Hebrew, Persian, Urdu → RTL)
 * - Initial direction is LTR; updated dynamically as language changes
 *
 * All layout and spacing in this app MUST use CSS logical properties
 * (margin-inline-start, padding-inline-end, etc.) via Tailwind logical
 * plugin utilities (ms-*, me-*, ps-*, pe-*, etc.) — never physical
 * (margin-left, padding-right).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TextDirection = 'ltr' | 'rtl';

export interface ThemeContextValue {
  direction: TextDirection;
  /** Set direction explicitly */
  setDirection: (direction: TextDirection) => void;
  /** Set direction from a BCP 47 language code (e.g. 'ar', 'ar-SA', 'he') */
  setDirectionFromLanguage: (languageCode: string) => void;
  isRTL: boolean;
}

// ---------------------------------------------------------------------------
// RTL language detection
// ---------------------------------------------------------------------------

/** ISO 639-1 / BCP 47 language codes that use RTL script */
const RTL_LANGUAGE_CODES = new Set([
  'ar',   // Arabic
  'arc',  // Aramaic
  'ckb',  // Central Kurdish (Sorani)
  'dv',   // Divehi / Maldivian
  'fa',   // Persian / Farsi
  'ha',   // Hausa (when written in Arabic script)
  'he',   // Hebrew
  'khw',  // Khowar
  'ks',   // Kashmiri
  'ps',   // Pashto
  'sd',   // Sindhi
  'sr',   // Serbian (when written in Arabic script)
  'ug',   // Uyghur
  'ur',   // Urdu
  'uz',   // Uzbek (when written in Arabic script)
  'yi',   // Yiddish
]);

export function getDirectionFromLanguage(languageCode: string): TextDirection {
  if (!languageCode) return 'ltr';
  // Extract the primary language subtag (e.g. 'ar' from 'ar-SA')
  const primary = languageCode.split('-')[0].toLowerCase();
  return RTL_LANGUAGE_CODES.has(primary) ? 'rtl' : 'ltr';
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [direction, setDirectionState] = useState<TextDirection>('ltr');

  // Apply direction to the root HTML element
  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', direction === 'rtl' ? 'ar' : 'en');
  }, [direction]);

  const setDirection = useCallback((dir: TextDirection) => {
    setDirectionState(dir);
  }, []);

  const setDirectionFromLanguage = useCallback((languageCode: string) => {
    const dir = getDirectionFromLanguage(languageCode);
    setDirectionState(dir);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      direction,
      setDirection,
      setDirectionFromLanguage,
      isRTL: direction === 'rtl',
    }),
    [direction, setDirection, setDirectionFromLanguage],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}

/** Convenience hook for direction-aware logic */
export function useDirection(): TextDirection {
  return useTheme().direction;
}
